import {type KeyboardEvent, type ReactNode, useRef, useState} from 'react';
import {format} from 'date-fns';
import {Check, ChevronLeft, ChevronRight, Copy, Film, Tag, X} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useCopy
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyableUrl, CopyUrlButton} from '@/components/shared/CopyableUrl';
import {ImagesPurgedPlaceholder} from '@/components/shared/ImagesPurgedPlaceholder';
import {type Item} from '@/api/items';
import {ItemLanguage, ItemStats} from '@/components/items/ItemStats';
import {RdSignalsSection} from '@/components/items/RdSignalsSection';
import {
  BLUR_IMAGE_CLASS,
  SensitiveMediaControls,
  useSensitiveMedia
} from '@/components/shared/SensitiveMedia';
import {Thumbnail, useThumbnailLoadFailed} from '@/components/shared/Thumbnail';
import {getItemCardModel, parseItemDate, VARIANT_LABEL} from '@/lib/itemCard';
import {getDeclaredLanguageSubtag, getMetaFields, type MetadataField} from '@/lib/itemMetadata';
import {itemPlatformId} from '@/lib/itemIds';
import {cn} from '@/lib/utils';
import {useTranslatedText, useTranslatedTexts} from '@/store/translationStore';
import {
  ITEM_DETAILS_WIDTH_DEFAULT,
  ITEM_DETAILS_WIDTH_MAX,
  ITEM_DETAILS_WIDTH_MIN,
  useUiStore
} from '@/store/uiStore';

// Shown in full above the metadata list, so not repeated in it.
const SHOWN_ABOVE = new Set(['title', 'description', 'bio', 'tags', 'keywords']);

function Section({title, children}: {readonly title: string; readonly children: ReactNode}) {
  return (
    <section className="flex flex-col gap-1.5">
      <h4 className="text-muted-foreground m-0 text-[10px] font-semibold tracking-widest uppercase">
        {title}
      </h4>
      {children}
    </section>
  );
}

function FieldValue({field}: {readonly field: MetadataField}) {
  const {Content: content} = field;
  if (content === null || content === undefined || content === '') {
    return <span className="text-muted-foreground">—</span>;
  }

  if (typeof content === 'boolean') {
    return <span>{content ? 'Yes' : 'No'}</span>;
  }

  const text = typeof content === 'string' ? content : JSON.stringify(content);
  return /^https?:\/\//i.test(text) ? <CopyableUrl url={text} /> : <span>{text}</span>;
}

function DateValue({raw}: {readonly raw: string | null}) {
  const date = parseItemDate(raw);
  return date ? (
    <span title={raw ?? undefined}>{format(date, 'd MMM yyyy, HH:mm')}</span>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

/**
 The "Show more" panel's content: everything the card had to clamp or leave out - the full
 description, every tag, and every metadata field the crawler wrote - in one scrollable column.
 ItemDetailsSheet owns the modal and the width.
 */
function ItemDetailPanel({
  item,
  pager,
  onClassify
}: {
  readonly item: Item;
  readonly pager: ReactNode;
  readonly onClassify: ((item: Item) => void) | undefined;
}) {
  const model = getItemCardModel(item);
  const {copy: copyId, isCopied: idCopied} = useCopy({value: () => itemPlatformId(item)});
  const name = useTranslatedText(item.id, model.name) ?? model.name;

  return (
    <div className="bg-card text-card-foreground flex h-full min-h-0 flex-col">
      <header className="border-border flex shrink-0 items-start gap-2 border-b px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
            {VARIANT_LABEL[model.variant]}
          </span>
          <SheetTitle className="m-0 text-sm leading-snug font-medium text-pretty break-words">
            {name}
          </SheetTitle>
          <SheetDescription className="sr-only">
            The full description, tags and every metadata field collected for this item.
          </SheetDescription>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {pager}
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Copy ${VARIANT_LABEL[model.variant].toLowerCase()} ID`}
                  onClick={() => {
                    void copyId();
                  }}
                />
              }>
              {idCopied ? <Check /> : <Copy />}
            </TooltipTrigger>
            <TooltipContent>{idCopied ? 'Copied' : 'Copy ID'}</TooltipContent>
          </Tooltip>
          {item.url ? <CopyUrlButton url={item.url} size="icon-sm" /> : null}
          <SheetClose render={<Button variant="ghost" size="icon-sm" aria-label="Close details" />}>
            <X />
          </SheetClose>
        </div>
      </header>

      <ItemDetailBody key={item.id} item={item} />
      {/* Outside the scrolling body, so the action stays in reach however far the analyst reads. */}
      {onClassify ? (
        <footer className="border-border bg-card flex shrink-0 items-center justify-end gap-2 border-t px-4 py-3">
          <Button
            onClick={() => {
              onClassify(item);
            }}>
            <Tag />
            Classify
          </Button>
        </footer>
      ) : null}
    </div>
  );
}

/**
 The panel's scrolling body. Keyed by item, so stepping to the next one starts it at the top with its
 image blurred again, while the header - and the pager the analyst is pressing - stays mounted.
 */
function ItemDetailBody({item}: {readonly item: Item}) {
  const model = getItemCardModel(item);
  const {thumbnailProps, controls} = useSensitiveMedia();
  const name = useTranslatedText(item.id, model.name) ?? model.name;
  const description = useTranslatedText(item.id, model.description) ?? '';
  const tags = useTranslatedTexts(item.id, [...model.tags]);
  const isActor = model.variant === 'channel' || model.variant === 'profile';
  const otherFields = getMetaFields(item.metadata).filter(
    (field) => !SHOWN_ABOVE.has(field.FieldName.toLowerCase())
  );
  const initial = (item.author_handle ?? item.author_name ?? '?').charAt(0).toUpperCase();
  const avatarLoad = useThumbnailLoadFailed(item.thumbnail_url);

  return (
    <div className="flex min-h-0 flex-1 [scrollbar-width:thin] flex-col gap-5 overflow-y-auto p-4">
      <div
        className={cn(
          'bg-card border-border relative shrink-0 overflow-hidden rounded-lg border',
          isActor ? 'flex items-center justify-center py-6' : 'aspect-video',
          model.variant === 'pin' && 'aspect-[4/5] max-h-96'
        )}>
        {item.thumbnail_url && !(isActor && avatarLoad.failed) ? (
          isActor ? (
            <Avatar className="size-24 after:pointer-events-none">
              <Thumbnail
                src={item.thumbnail_url}
                storedSha256={item.stored_thumbnail_sha256}
                alt={name}
                className="aspect-square size-full rounded-full object-cover"
                onError={avatarLoad.onError}
                {...thumbnailProps}
              />
            </Avatar>
          ) : (
            <Thumbnail
              src={item.thumbnail_url}
              storedSha256={item.stored_thumbnail_sha256}
              alt={name}
              className={cn(
                'h-full w-full',
                BLUR_IMAGE_CLASS,
                model.variant === 'pin' ? 'object-contain' : 'object-cover'
              )}
              {...thumbnailProps}
            />
          )
        ) : item.images_purged ? (
          <ImagesPurgedPlaceholder reason={item.images_purged_reason} />
        ) : isActor ? (
          <Avatar aria-hidden="true" className="size-24">
            <AvatarFallback variant="primary" className="text-3xl">
              {initial}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className="text-muted-foreground flex h-full items-center justify-center">
            <Film className="size-8" />
          </div>
        )}
        {item.thumbnail_url && !(isActor && avatarLoad.failed) ? (
          <SensitiveMediaControls {...controls} />
        ) : null}
        {model.duration ? (
          <Badge variant="secondary" className="absolute right-2 bottom-2 tabular-nums">
            {model.duration}
          </Badge>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <ItemStats
          stats={model.stats}
          trailing={
            <ItemLanguage
              offerTranslate={getDeclaredLanguageSubtag(item.metadata) !== 'en'}
              itemKey={item.id}
              language={model.language}
              texts={[model.name, model.description, ...model.tags]}
            />
          }
        />
      </div>

      {description ? (
        <Section title={model.variant === 'profile' ? 'Bio' : 'Description'}>
          <p className="m-0 text-sm leading-relaxed break-words whitespace-pre-wrap">
            {description}
          </p>
        </Section>
      ) : null}

      {tags.length > 0 ? (
        <Section title={model.variant === 'channel' ? 'Keywords' : 'Tags'}>
          <div className="flex flex-wrap gap-1">
            {model.tags.map((raw, i) => (
              <Badge key={raw} variant="tag">
                {tags[i]}
              </Badge>
            ))}
          </div>
        </Section>
      ) : null}

      {item.rd_assessment === undefined ? null : (
        <RdSignalsSection detailed itemId={item.id} assessment={item.rd_assessment} />
      )}

      <Section title="Item">
        <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-xs">
          <dt className="text-muted-foreground">Author</dt>
          <dd className="m-0 flex min-w-0 items-center gap-1.5">
            <Avatar size="sm" aria-hidden="true">
              <AvatarFallback>{initial}</AvatarFallback>
            </Avatar>
            <span className="truncate">
              {item.author_name ?? item.author_handle ?? '—'}
              {item.author_name && item.author_handle ? (
                <span className="text-muted-foreground"> · {item.author_handle}</span>
              ) : null}
            </span>
          </dd>
          <dt className="text-muted-foreground">Platform</dt>
          <dd className="m-0 capitalize">{item.platform}</dd>
          {item.published_at ? (
            <>
              <dt className="text-muted-foreground">Published</dt>
              <dd className="m-0">
                <DateValue raw={item.published_at} />
              </dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Collected</dt>
          <dd className="m-0">
            <DateValue raw={item.created_at} />
          </dd>
          <dt className="text-muted-foreground">Platform ID</dt>
          <dd className="m-0 font-mono break-all">{item.external_id}</dd>
          {item.author_id && item.author_id !== item.external_id ? (
            <>
              <dt className="text-muted-foreground">Author ID</dt>
              <dd className="m-0 font-mono break-all">{item.author_id}</dd>
            </>
          ) : null}
          {item.url ? (
            <>
              <dt className="text-muted-foreground">URL</dt>
              <dd className="m-0 min-w-0">
                <CopyableUrl url={item.url} />
              </dd>
            </>
          ) : null}
        </dl>
      </Section>

      {otherFields.length > 0 ? (
        <Section title="Metadata">
          <dl className="m-0 grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-xs">
            {otherFields.map((field, i) => (
              // A field name can repeat across a re-crawl merge; the position keeps keys unique.
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${field.FieldName}-${i}`} className="contents">
                <dt className="text-muted-foreground break-words">{field.FieldName}</dt>
                <dd className="m-0 min-w-0 [overflow-wrap:anywhere] break-words">
                  <FieldValue field={field} />
                </dd>
              </div>
            ))}
          </dl>
        </Section>
      ) : null}
    </div>
  );
}

export type ItemDetailsSheetProps = {
  // The item to show; null closes the sheet.
  readonly item: Item | null;
  // The items it was opened among, in on-screen order, for back/forward. Omit for no pager.
  readonly items?: readonly Item[];
  readonly onNavigate?: (item: Item) => void;
  // Offers Classify in the sheet's footer. The sheet stays open beneath the classify dialog, so the
  // analyst can carry on stepping through the items afterwards.
  readonly onClassify?: (item: Item) => void;
  readonly onClose: () => void;
};

/**
 Back/forward through the view the sheet was opened from, as the design system's Pagination, icons
 only: the panel is narrow, and the position between the arrows says what the arrows do.
 */
function ItemPager({
  index,
  count,
  onStep
}: {
  readonly index: number;
  readonly count: number;
  readonly onStep: (delta: -1 | 1) => void;
}) {
  return (
    <Pagination aria-label="Items in this view" className="mx-0 w-auto">
      <PaginationContent>
        <PaginationItem>
          <Tooltip>
            <TooltipTrigger
              render={
                <PaginationLink
                  size="icon-sm"
                  aria-label="Previous item"
                  // Without an href the link is a native button and forwards `disabled`, but the
                  // package types the props as an anchor's, so it goes past the type as a spread.
                  {...{disabled: index <= 0}}
                  onClick={() => {
                    onStep(-1);
                  }}
                />
              }>
              <ChevronLeft />
            </TooltipTrigger>
            <TooltipContent>Previous item (←)</TooltipContent>
          </Tooltip>
        </PaginationItem>
        <PaginationItem className="text-muted-foreground px-1 text-xs tabular-nums">
          {index + 1} of {count}
        </PaginationItem>
        <PaginationItem>
          <Tooltip>
            <TooltipTrigger
              render={
                <PaginationLink
                  size="icon-sm"
                  aria-label="Next item"
                  {...{disabled: index >= count - 1}}
                  onClick={() => {
                    onStep(1);
                  }}
                />
              }>
              <ChevronRight />
            </TooltipTrigger>
            <TooltipContent>Next item (→)</TooltipContent>
          </Tooltip>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

// Arrow keys step through the items, except where an arrow already means something: text entry, and
// the resize handle (which moves with them).
function isArrowTarget(target: EventTarget): boolean {
  return (
    target instanceof HTMLElement &&
    !target.closest('input, textarea, select, [contenteditable="true"], [role="separator"]')
  );
}

/**
 Show more, as a modal sidebar: the panel slides in over the page from the right and the page
 behind it is blurred (the Sheet's own backdrop). The Sheet's popup fills the viewport so the panel
 can sit in a resizable group - a transparent spacer on the left, the drag handle, then the panel -
 and the spacer closes it, standing in for the backdrop click the popup now covers. That needs the
 popup's max width lifted as well as its width: the Sheet caps a side sheet at max-w-sm, which left
 no room for the spacer (a click on the blur hit the backdrop, which is ignored) and pinned the panel
 near its minimum. The width is the analyst's own and persists, like the investigations rail's.
 */
export function ItemDetailsSheet({
  item,
  items,
  onNavigate,
  onClassify,
  onClose
}: ItemDetailsSheetProps) {
  const width = useUiStore((s) => s.itemDetailsWidth);
  const setWidth = useUiStore((s) => s.setItemDetailsWidth);
  // The live width during a drag, committed to the store once it ends (not on every pointer move).
  const liveWidthRef = useRef<number | null>(null);
  const panelRef = useRef<{resize: (size: number) => void} | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  // Kept through the close animation, so the panel doesn't empty while it slides out.
  const [shown, setShown] = useState(item);
  if (item && item !== shown) {
    setShown(item);
  }

  const index = shown && items ? items.findIndex((i) => i.id === shown.id) : -1;
  const step = (delta: -1 | 1) => {
    const next = items?.[index + delta];
    if (index >= 0 && next) {
      onNavigate?.(next);
    }
  };

  const pager =
    items && onNavigate && index >= 0 && items.length > 1 ? (
      <ItemPager index={index} count={items.length} onStep={step} />
    ) : null;

  return (
    <Sheet
      open={item !== null}
      onOpenChange={(open, details) => {
        if (open) {
          return;
        }

        // The popup fills the viewport (the spacer is what closes it on a click), so an outside press
        // or an Escape from outside it can only come from a dialog opened over it - Classify - and
        // must not take the sheet down with it.
        const fromOutside = !popupRef.current?.contains(document.activeElement ?? null);
        if (
          details.reason === 'outside-press' ||
          (details.reason === 'escape-key' && fromOutside)
        ) {
          return;
        }

        onClose();
      }}>
      <SheetContent
        ref={popupRef}
        side="right"
        showCloseButton={false}
        className="w-full! max-w-none! border-0! bg-transparent shadow-none"
        onKeyDown={(event: KeyboardEvent) => {
          if (!pager || !isArrowTarget(event.target)) {
            return;
          }

          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            step(event.key === 'ArrowLeft' ? -1 : 1);
          }
        }}>
        <ResizablePanelGroup
          className="h-full"
          onLayoutChanged={(_layout, meta) => {
            if (meta.isUserInteraction && liveWidthRef.current !== null) {
              setWidth(liveWidthRef.current);
            }
          }}>
          <ResizablePanel id="item-details-spacer">
            <div aria-hidden="true" className="h-full" onClick={onClose} />
          </ResizablePanel>
          <ResizableHandle
            aria-label="Resize item details"
            title="Drag to resize, double-click to reset"
            onDoubleClick={() => {
              panelRef.current?.resize(ITEM_DETAILS_WIDTH_DEFAULT);
              setWidth(ITEM_DETAILS_WIDTH_DEFAULT);
            }}
          />
          <ResizablePanel
            id="item-details-panel"
            defaultSize={width}
            minSize={ITEM_DETAILS_WIDTH_MIN}
            maxSize={ITEM_DETAILS_WIDTH_MAX}
            groupResizeBehavior="preserve-pixel-size"
            className="shadow-lg"
            panelRef={(handle) => {
              panelRef.current = handle;
            }}
            onResize={(size) => {
              liveWidthRef.current = size.inPixels;
            }}>
            {shown ? <ItemDetailPanel item={shown} pager={pager} onClassify={onClassify} /> : null}
          </ResizablePanel>
        </ResizablePanelGroup>
      </SheetContent>
    </Sheet>
  );
}
